import React, { useEffect, useRef, useState } from 'react';
import { Brain, Lightbulb, Target, Puzzle } from 'lucide-react';
import { GradientTitle } from '../ui';

interface USPSectionProps {
  showTitle?: boolean;
  useH1?: boolean;
}

export const USPSection: React.FC<USPSectionProps> = ({ showTitle = true, useH1 = true }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  const features = [
    {
      icon: Brain,
      title: "Research-Based",
      description: "Built on proven psychology studies"
    },
    {
      icon: Lightbulb,
      title: "Practical",
      description: "Apply to real-world decisions"
    },
    {
      icon: Target,
      title: "Interactive",
      description: "Learn through exercises"
    },
    {
      icon: Puzzle,
      title: "Comprehensive",
      description: "Cover all major biases"
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    let animationFrameId: number;
    let scrollPosition = 0;

    const scroll = () => {
      if (scrollContainer && isMobile) {
        scrollPosition += 0.5;
        if (scrollPosition >= scrollContainer.scrollWidth / 2) {
          scrollPosition = 0;
        }
        scrollContainer.scrollLeft = scrollPosition;
        animationFrameId = requestAnimationFrame(scroll);
      }
    };

    if (isMobile) {
      animationFrameId = requestAnimationFrame(scroll);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isMobile]);

  return (
    <section className="py-12">
      <div className="container mx-auto px-4">
        {showTitle && (
          <GradientTitle className="text-3xl md:text-4xl text-center mb-12" useH1={useH1}>
            Why Choose Cognitive Bias Lab?
          </GradientTitle>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100 p-6 max-w-5xl mx-auto">
          <div 
            ref={scrollRef}
            className="overflow-hidden lg:overflow-visible"
          >
            <div className="flex lg:grid lg:grid-cols-4 gap-4 lg:gap-8 min-w-max lg:min-w-0 
              pointer-events-none lg:pointer-events-auto"
            >
              {(isMobile ? [...features, ...features] : features).map((feature, index) => (
                <div 
                  key={index}
                  className="flex flex-col items-center text-center w-48 lg:w-auto px-2 lg:px-4 
                    transition-transform duration-300 hover:scale-105"
                >
                  <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 
                    flex items-center justify-center mb-2">
                    <feature.icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}; 